import * as React from 'react';
import {useRef, useState, useCallback, useEffect} from 'react';
import {KatButton, KatDropdown, KatModal} from '@amzn/katal-react';
import {ReviewResult} from 'src/app/constant/Enums';
import {MAX_COMMENT_LENGTH} from 'src/app/api/model/request/AddCommentRequest';

export interface DecisionFooterProps {
    /** Available decision options (e.g. ['OFFBOARDED', 'NO_ACTION']). */
    decisions: string[];
    /** Reason code dropdown options. */
    reasonCodes: {name: string; value: string}[];
    /** Callback when a decision is confirmed. */
    onSubmit: (decision: string, notes: string, reasonCode: string) => void;
    /** Disables all controls (e.g. during submission or in readOnly mode). */
    disabled: boolean;
    /** Called when the "+ Upload File" button is clicked. Opens the upload modal (owned by the parent). */
    onUploadClick?: () => void;
    /** Called when "Add Remark" is clicked, with the current Auditor Remarks text. Parent posts it as a comment; resolves true on success (remarks are then cleared). */
    onAddRemark?: (remark: string) => Promise<boolean>;
}

// Decision-code → button label. Keys are ReviewResult tokens (the values the backend serves in
// `decisions` and that SubmitDecision accepts), so they must match the model enum exactly.
const DECISION_LABELS: Record<string, string> = {
    [ReviewResult.OFFBOARDED]: 'Offboard DP',
    [ReviewResult.NO_ACTION]: 'No Action',
    [ReviewResult.RE_INSTATED]: 'Reinstate DP',
    [ReviewResult.NO_RE_INSTATED]: 'Do Not Reinstate',
    [ReviewResult.INACTIVATED]: 'Inactivate'
};

const DESTRUCTIVE_DECISIONS = new Set<string>([ReviewResult.OFFBOARDED, ReviewResult.INACTIVATED]);

/** Min/max height for the drag-resizable textarea (px). */
const MIN_TEXTAREA_HEIGHT = 40;
const MAX_TEXTAREA_HEIGHT = 500;

/**
 * Returns a human-readable label for a decision code.
 */
export const getButtonLabel = (decision: string): string => {
    return DECISION_LABELS[decision] || decision.replace(/_/g, ' ');
};

/**
 * Decision action bar at the bottom of the review page.
 * Layout:
 *   Row 1: Auditor Remarks (resizable) | Reason dropdown | Decision buttons
 *   Row 2: Upload File placeholder button
 * Shows a confirmation modal before submitting.
 */
const DecisionFooter: React.FC<DecisionFooterProps> = ({decisions, reasonCodes, onSubmit, disabled, onUploadClick, onAddRemark}) => {
    const notesRef = useRef<HTMLTextAreaElement>(null);
    const reasonCodeRef = useRef<any>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingDecision, setPendingDecision] = useState<string | null>(null);
    const [textareaHeight, setTextareaHeight] = useState(MIN_TEXTAREA_HEIGHT);
    // Live character count for the Auditor Remarks, shown next to the label so auditors
    // know how close they are to MAX_COMMENT_LENGTH (the same limit the submit validation enforces).
    const [notesLength, setNotesLength] = useState(0);

    // Drag-to-resize state
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startHeight = useRef(MIN_TEXTAREA_HEIGHT);

    const handleDragMove = useCallback((e: MouseEvent) => {
        if (!isDragging.current) return;
        const delta = startY.current - e.clientY;
        const newHeight = Math.max(MIN_TEXTAREA_HEIGHT, Math.min(startHeight.current + delta, MAX_TEXTAREA_HEIGHT));
        setTextareaHeight(newHeight);
    }, []);

    const handleDragEnd = useCallback(() => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
    }, [handleDragMove]);

    const handleDragStart = useCallback((e: React.MouseEvent) => {
        isDragging.current = true;
        startY.current = e.clientY;
        startHeight.current = textareaHeight;
        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
        e.preventDefault();
    }, [textareaHeight, handleDragMove, handleDragEnd]);

    // Cleanup document-level listeners on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [handleDragMove, handleDragEnd]);

    const handleDecisionClick = (decision: string) => {
        setPendingDecision(decision);
        setShowConfirmation(true);
    };

    const handleConfirm = () => {
        if (pendingDecision) {
            const notes = notesRef.current?.value || '';
            const reason = reasonCodeRef.current?.value || '';
            onSubmit(pendingDecision, notes, reason);
        }
        setShowConfirmation(false);
        setPendingDecision(null);
    };

    const handleCancel = () => {
        setShowConfirmation(false);
        setPendingDecision(null);
    };

    const handleUploadClick = () => {
        onUploadClick?.();
    };

    const handleAddRemarkClick = async () => {
        if (!onAddRemark) return;
        const added = await onAddRemark(notesRef.current?.value || '');
        // Clear the Auditor Remarks only after a successful add.
        if (added && notesRef.current) {
            notesRef.current.value = '';
            setNotesLength(0);
        }
    };

    return (
        <div data-testid="decision-footer">
            {/* Row 1: Remarks + Reason + Buttons */}
            <div className="lighthouse-decision-row">
                <div className="lighthouse-remarks-wrapper">
                    <div className="lighthouse-remarks-header">
                        <label className="lighthouse-remarks-label" htmlFor="auditor-remarks">
                            Auditor Remarks
                        </label>
                        <span
                            className="lighthouse-remarks-drag-handle"
                            onMouseDown={handleDragStart}
                            title="Drag to resize"
                            data-testid="drag-handle"
                        >
                            ⋮⋮
                        </span>
                    </div>
                    <textarea
                        id="auditor-remarks"
                        className="lighthouse-remarks-textarea"
                        style={{height: `${textareaHeight}px`}}
                        disabled={disabled}
                        ref={notesRef}
                        maxLength={MAX_COMMENT_LENGTH}
                        onChange={(e) => setNotesLength(e.target.value.length)}
                        placeholder="Enter remarks..."
                        data-testid="auditor-remarks"
                    />
                    <span
                        className={`lighthouse-remarks-charcount${
                            notesLength >= MAX_COMMENT_LENGTH ? ' lighthouse-remarks-charcount--limit' : ''
                        }`}
                        data-testid="remarks-charcount"
                    >
                        {notesLength} / {MAX_COMMENT_LENGTH}
                    </span>
                    {/* Overlay "Add" button pinned to the box's bottom-right (chat-style). */}
                    <KatButton
                        label="Add"
                        variant="primary"
                        onClick={handleAddRemarkClick}
                        disabled={disabled}
                        className="lighthouse-remark-btn"
                        data-testid="add-remark-btn"
                    />
                </div>
                <KatDropdown
                    placeholder="Reason"
                    options={reasonCodes}
                    disabled={disabled}
                    ref={reasonCodeRef}
                    data-testid="reason-code"
                />
                {decisions.map((decision) => (
                    <KatButton
                        key={decision}
                        label={getButtonLabel(decision)}
                        onClick={() => handleDecisionClick(decision)}
                        disabled={disabled}
                        className={DESTRUCTIVE_DECISIONS.has(decision) ? 'amazon-orange' : ''}
                        data-testid={`decision-btn-${decision}`}
                    />
                ))}
            </div>

            {/* Row 2: Upload */}
            <div className="lighthouse-upload-row">
                <KatButton
                    label="+ Upload File"
                    variant="secondary"
                    onClick={handleUploadClick}
                    disabled={disabled}
                    data-testid="upload-file-btn"
                />
            </div>

            {/* Confirmation Modal */}
            <KatModal
                visible={showConfirmation}
                onClose={handleCancel}
                data-testid="decision-confirmation-modal"
            >
                <span slot="title">Confirm Decision</span>
                <div>
                    <p>
                        Are you sure you want to submit the decision:{' '}
                        <strong>{pendingDecision ? getButtonLabel(pendingDecision) : ''}</strong>?
                    </p>
                    {notesRef.current?.value && (
                        <p style={{whiteSpace: 'pre-wrap'}}><em>Remarks: {notesRef.current.value}</em></p>
                    )}
                </div>
                <div slot="footer" className="lighthouse-modal-footer">
                    <KatButton label="Cancel" variant="link" onClick={handleCancel} data-testid="confirm-cancel" />
                    <KatButton label="Confirm" onClick={handleConfirm} className="amazon-orange" data-testid="confirm-submit" />
                </div>
            </KatModal>
        </div>
    );
};

export default DecisionFooter;
